import React, { useState } from 'react';
import { User, signOut } from 'firebase/auth';
import { auth } from '../firebase';
import SubscriptionManager from './SubscriptionManager';

interface UsageData {
  aiProcessingCount: number;
  manualSessionCount: number;
  subscriptionTier: 'free' | 'premium';
}

interface UserHeaderProps {
  user: User | null;
  onSignInClick: () => void;
  usageData?: UsageData;
}

const UserHeader: React.FC<UserHeaderProps> = ({ user, onSignInClick, usageData }) => {
  const [showSubscriptionManager, setShowSubscriptionManager] = useState(false);
  const handleSignOut = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  if (!user) {
    return (
      <div className="bg-surface-raised border-b border-border px-4 py-2.5">
        <div className="flex justify-between items-center max-w-7xl mx-auto">
          <div className="flex items-center gap-3">
            <span className="text-accent-400 font-bold text-lg tracking-tight">QME</span>
            <span className="text-content-tertiary text-sm hidden sm:inline">Ratings</span>
          </div>
          <button
            onClick={onSignInClick}
            className="btn-primary text-sm px-4 py-1.5"
          >
            Sign In
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-surface-raised border-b border-border px-4 py-2.5">
      <div className="flex justify-between items-center max-w-7xl mx-auto">
        <div className="flex items-center gap-4 text-sm">
          <div className="flex items-center gap-3">
            <span className="text-accent-400 font-bold text-lg tracking-tight">QME</span>
            <span className="text-content-tertiary text-sm hidden sm:inline">Ratings</span>
          </div>
          <span className="hidden sm:inline text-content-secondary">
            {user.email}
          </span>
          {usageData && (
            <div className="flex items-center gap-2 text-xs">
              {/* Premium is unlimited, so "AI: 9/∞" said nothing; free users see what is left. */}
              {usageData.subscriptionTier === 'premium' ? (
                <span className="bg-accent-900/30 text-accent-300 border border-accent-700/30 px-2 py-0.5 rounded">
                  Premium
                </span>
              ) : (
                <>
                  <span className="bg-accent-900/30 text-accent-300 border border-accent-700/30 px-2 py-0.5 rounded" title="AI exam runs left this month">
                    {Math.max(0, 25 - usageData.aiProcessingCount)} of 25 AI runs left
                  </span>
                  <span className="text-content-tertiary border border-border px-2 py-0.5 rounded" title="Manual editing sessions left this month">
                    {Math.max(0, 3 - usageData.manualSessionCount)} of 3 manual left
                  </span>
                </>
              )}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          {/* Admin Dashboard Link - only show for admin users */}
          {(user.email === 'admin@qmeratings.com' || user.email === 'jessedashe@gmail.com' || user.uid === 'IiIdS3uNSjdBJYlDVmwrhiehoXY2') && (
            <button
              onClick={() => window.open('/admin', '_blank')}
              className="px-3 py-1 rounded text-xs bg-red-500/10 text-red-400 border border-red-500/20 hover:bg-red-500/20 transition-colors"
              title="Admin Dashboard"
            >
              Admin
            </button>
          )}
          <button
            onClick={() => setShowSubscriptionManager(true)}
            className={`px-3 py-1 rounded text-sm transition-colors ${
              usageData?.subscriptionTier === 'premium'
                ? 'btn-secondary text-xs px-3 py-1'
                : 'btn-primary text-xs px-3 py-1'
            }`}
          >
            {usageData?.subscriptionTier === 'premium' ? 'Manage Subscription' : 'Upgrade'}
          </button>
          <button
            onClick={handleSignOut}
            className="text-content-tertiary hover:text-content text-sm transition-colors"
          >
            Sign Out
          </button>
        </div>
      </div>

      {/* Subscription Manager Modal */}
      <SubscriptionManager
        isOpen={showSubscriptionManager}
        onClose={() => setShowSubscriptionManager(false)}
      />
    </div>
  );
};

export default UserHeader;
